import makeStyles from '@mui/styles/makeStyles';
import { brandclub_colors } from '@stackline/ui';
import { Location } from 'history';
import React, { CSSProperties, useMemo } from 'react';
import {
  ATLAS_V2_TAB_INDICES,
  PRIMARY_TAB_INDICES
} from 'src/components/GlobalComponents/PrimaryPageNavigation/constants';
import styles from 'src/components/BeaconV2Components/styles/styles';
import { TabRoute } from 'src/components/CommonV2Components/GenericSideBar/types';
import { calculateNewQueryParams } from 'src/store/modules/app/selectors';

import { AppLinkTabs } from 'src/components/GlobalComponents/PrimaryPageNavigation/AppLinkTabs';
import { EntityPathname } from 'src/types/application/types';
import { useAppSelector } from 'src/utils/Hooks';
import { getAppSubscriptionTier, getQueryParamValue, shouldShowDigitalShelfTab } from 'src/utils/app';
import { encodeHash } from 'src/utils/hashIds';
import colors from 'src/utils/colors';

const useStyles = makeStyles({
  navbar: {
    backgroundColor: brandclub_colors.white,
    boxShadow: '0 1px 4px 0 rgba(21, 34, 50, 0.08)',
    position: 'initial',
    top: 60,
    zIndex: 1000
  },
  tabsContainer: {
    paddingTop: '8px',
    paddingBottom: '8px',
    WebkitMaskImage: 'none'
  },
  tabs: {
    ...styles.pageCenterDiv,
    width: styles.pageCenterDiv.width,
    margin: `0 auto`,
    '& div.MuiTabs-flexContainer': {
      alignItems: 'center',
      paddingLeft: 0,
      overflowX: 'hidden',
      gap: '18px'
    }
  },
  tab: {
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: 'Roboto',
    color: colors.darkBlue,
    padding: 0,
    textTransform: 'none',
    '&.MuiButtonBase-root': {
      minHeight: 26,
      minWidth: 'unset',
      textDecoration: 'none'
    },
    '&.Mui-selected': {
      borderRadius: 13,
      padding: '4px 16px',
      backgroundColor: colors.darkBlue,
      color: brandclub_colors.white
    }
  }
});

interface LinkTabProps {
  label: string;
  route: string;
  routePrefix?: string;
  containerStyle?: React.ComponentProps<'span'>['style'];
}

interface AppLinkTabProps {
  tabIndex: number;
  setTabIndex: (num: number) => void;
  tabRoutes: TabRoute[];
  tabsProps: any;
  tabProps: any;
  containerStyle?: CSSProperties;
}

interface PrimaryNavBarProps {
  location: Location;
}

export const AtlasV2PrimaryNav = ({ location }: PrimaryNavBarProps) => {
  const app = useAppSelector((state) => state.app);
  const retailer = useAppSelector((state) => state.retailer);
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const comparisonTimePeriod = useAppSelector((state) => state.comparisonTimePeriod);
  const allWeekIdsByRetailerId = useAppSelector((state) => state.allWeekIdsByRetailerId);

  const requireSubscription = getAppSubscriptionTier() !== 'prime';
  const entityType = getQueryParamValue('entityType');
  const action = getQueryParamValue('action');
  const entityIdBeingEdited = getQueryParamValue('id');
  const searchPage = !!getQueryParamValue('searchPage');

  const tabRoutes: TabRoute[] = [
    {
      label: 'Home',
      route: `/company/${encodeHash(0)}${
        calculateNewQueryParams(
          app,
          { id: retailer.id === '0' ? Object.keys(allWeekIdsByRetailerId)[1] : retailer.id },
          mainTimePeriod,
          comparisonTimePeriod
        ).searchParams
      }&tab=${app.defaultQueryParams.tab}&subtab=${app.defaultQueryParams.subtab}`,
      index: ATLAS_V2_TAB_INDICES.Home
    },
    {
      label: 'Categories',
      route: `/home${
        calculateNewQueryParams(
          app,
          { id: retailer.id === '0' ? Object.keys(allWeekIdsByRetailerId)[1] : retailer.id },
          mainTimePeriod,
          comparisonTimePeriod
        ).searchParams
      }&tab=summary&subtab=${app.name}&entityType=category`,
      index: ATLAS_V2_TAB_INDICES.Categories
    },
    {
      label: 'Segments',
      route: `/home${
        calculateNewQueryParams(
          app,
          { id: retailer.id === '0' ? Object.keys(allWeekIdsByRetailerId)[1] : retailer.id },
          mainTimePeriod,
          comparisonTimePeriod
        ).searchParams
      }&tab=summary&subtab=${app.name}&entityType=segment`,
      index: ATLAS_V2_TAB_INDICES.Segments
    },
    {
      requireSubscription,
      label: 'Topics',
      route: `/home${
        calculateNewQueryParams(
          app,
          {
            id: retailer.id === '0' ? Object.keys(allWeekIdsByRetailerId)[1] : retailer.id
          },
          mainTimePeriod,
          comparisonTimePeriod
        ).searchParams
      }&tab=summary&subtab=${app.name}&entityType=searchtermlist`,
      index: PRIMARY_TAB_INDICES.Topics
    },

    {
      label: 'Brands',
      route: `/home${
        calculateNewQueryParams(
          app,
          { id: retailer.id === '0' ? Object.keys(allWeekIdsByRetailerId)[1] : retailer.id },
          mainTimePeriod,
          comparisonTimePeriod
        ).searchParams
      }&tab=summary&subtab=${app.name}&entityType=brand`,
      index: ATLAS_V2_TAB_INDICES.Brands
    }
  ];

  if (shouldShowDigitalShelfTab()) {
      tabRoutes.push({
        label: 'Shelf',
        route: `/home${
          calculateNewQueryParams(
            app,
            { id: retailer.id === '0' ? Object.keys(allWeekIdsByRetailerId)[1] : retailer.id },
            mainTimePeriod,
            comparisonTimePeriod
          ).searchParams
        }&tab=summary&subtab=${app.name}&entityType=shelf`,
        index: ATLAS_V2_TAB_INDICES.Shelf
      });
    }

  const activeTabIndex = useMemo(() => {
    if (location.pathname === EntityPathname.Search) {
      if (searchPage) {
        return undefined;
      }
      if (entityType === 'segment') {
        return ATLAS_V2_TAB_INDICES.Segments;
      } else if (entityType === 'searchtermlist') {
        return ATLAS_V2_TAB_INDICES.Topics;
      } else {
        return ATLAS_V2_TAB_INDICES.Home;
      }
    }

    if (location.pathname === EntityPathname.Home) {
      switch (entityType) {
        case 'category':
          return ATLAS_V2_TAB_INDICES.Categories;
        case 'segment':
          return ATLAS_V2_TAB_INDICES.Segments;
        case 'searchtermlist':
          return ATLAS_V2_TAB_INDICES.Topics;
        case 'brand':
          return ATLAS_V2_TAB_INDICES.Brands;
        case 'shelf':
          return ATLAS_V2_TAB_INDICES.Shelf;
        default:
          return ATLAS_V2_TAB_INDICES.Home;
      }
    }

    // If we can't find the active tab, default to the first tab (home)
    return PRIMARY_TAB_INDICES.Home;
  }, [location.pathname, entityType, action, entityIdBeingEdited]);

  const classes = useStyles();
  return (
    <div className={classes.navbar}>
      <div className={classes.tabsContainer}>
        <AppLinkTabs
          tabIndex={activeTabIndex}
          setTabIndex={(_) => {}}
          tabRoutes={tabRoutes}
          tabsProps={{
            classes: {
              root: classes.tabs
            }
          }}
          tabProps={{
            classes: {
              root: classes.tab
            }
          }}
        />
      </div>
    </div>
  );
};
